import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Field, Input, Textarea, LoadingState, Badge } from "@/components/design-system";
import { formatApiError } from "@/lib/api";
import { listOwnProducts } from "@/lib/products";
import { listTemplates, createCampaign } from "@/lib/campaigns";

const cn = (...xs) => xs.filter(Boolean).join(" ");

export default function CampaignNew() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialProduct = sp.get("productId") || "";

  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    product_id: initialProduct,
    template_id: "",
    name: "",
    description: "",
    target_responses: 25,
  });

  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([listOwnProducts(), listTemplates()]);
        setProducts(p);
        setTemplates(t);
        if (!form.product_id && p.length === 1) setForm((f) => ({ ...f, product_id: p[0].id }));
      } catch (err) { setError(formatApiError(err)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chosenTemplate = useMemo(() => templates.find((t) => t.id === form.template_id) || null, [templates, form.template_id]);
  const chosenProduct = useMemo(() => products.find((p) => p.id === form.product_id) || null, [products, form.product_id]);

  const disabled = !form.product_id || !form.template_id || form.name.length < 3;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const c = await createCampaign(form);
      nav(`/app/campaigns/${c.id}`, { replace: true });
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <LoadingState label="Loading campaign builder…" />;

  return (
    <div data-testid="campaign-new-page" className="max-w-4xl">
      <div className="mb-6"><Link to="/app/campaigns" className="text-sm text-[#2D7FF9] font-medium">← Back to campaigns</Link></div>
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">New campaign</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">Start a validation campaign</h1>
      <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">Pick a product, choose a template, and give the community a clear question to answer.</p>

      <form onSubmit={onSubmit} className="mt-8 space-y-6">
        <Card>
          <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">1 · Product</h2>
          {products.length === 0 ? (
            <p className="mt-3 text-sm text-[#0B1F3A]/75">You don't have any products yet. <Link to="/app/products/new" className="text-[#2D7FF9] font-semibold">Add one first →</Link></p>
          ) : (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`campaign-pick-product-${p.slug}`}
                  onClick={() => setForm({ ...form, product_id: p.id })}
                  className={cn("text-left rounded-2xl border-2 p-4 transition-all", form.product_id === p.id ? "border-[#2D7FF9] bg-[#E8F1FF]" : "border-[#0B1F3A]/15 hover:border-[#0B1F3A]/30 bg-white")}
                >
                  <div className="flex items-center gap-2">
                    <Badge variant={p.status === "published" ? "active" : "neutral"}>{p.status}</Badge>
                  </div>
                  <div className="font-[Montserrat] font-bold text-lg text-[#0B1F3A] mt-2 truncate">{p.name}</div>
                  <div className="text-xs text-[#0B1F3A]/60 mt-1 line-clamp-2">{p.tagline}</div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">2 · Template</h2>
          <p className="text-sm text-[#0B1F3A]/70 mt-1">Snapshots the question set now — later edits to the template won't affect this campaign.</p>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`campaign-pick-template-${t.id}`}
                onClick={() => setForm({ ...form, template_id: t.id })}
                className={cn("text-left rounded-2xl border-2 p-5 transition-all", form.template_id === t.id ? "border-[#2D7FF9] bg-[#E8F1FF]" : "border-[#0B1F3A]/15 hover:border-[#0B1F3A]/30 bg-white")}
              >
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">v{t.version}</div>
                <div className="font-[Montserrat] font-bold text-lg text-[#0B1F3A] mt-1">{t.name}</div>
                <div className="text-xs text-[#0B1F3A]/70 mt-2 leading-relaxed">{t.description}</div>
                <div className="mt-3 text-[11px] text-[#0B1F3A]/60">{t.questions.length} questions</div>
              </button>
            ))}
          </div>
          {chosenTemplate && (
            <div className="mt-6 border-t border-[#0B1F3A]/10 pt-6">
              <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Question preview</h3>
              <ol className="mt-3 space-y-2 text-sm">
                {chosenTemplate.questions.map((q, i) => (
                  <li key={q.id} className="flex gap-3 text-[#0B1F3A]/85">
                    <span className="font-bold text-[#2D7FF9] w-6 shrink-0">{i+1}.</span>
                    <span>{q.text} <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/50">· {q.type}{q.required ? " · required" : ""}</span></span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </Card>

        <Card>
          <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">3 · Details</h2>
          <div className="mt-4 space-y-4">
            <Field label="Campaign name" htmlFor="c-name" hint="Between 3 and 120 characters.">
              <Input id="c-name" data-testid="campaign-name" required minLength={3} maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Founding customer interviews" />
            </Field>
            <Field label="Optional briefing for reviewers" htmlFor="c-desc">
              <Textarea id="c-desc" data-testid="campaign-description" maxLength={2000} rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Anything you want reviewers to keep in mind (context, target user, current stage…)" />
            </Field>
            <Field label="Target responses (optional)" htmlFor="c-target" hint="If set, the campaign auto-closes when reached.">
              <Input id="c-target" data-testid="campaign-target" type="number" min={1} max={1000} value={form.target_responses ?? ""} onChange={(e) => setForm({ ...form, target_responses: e.target.value === "" ? null : parseInt(e.target.value, 10) })} />
            </Field>
          </div>
        </Card>

        {error && <p data-testid="campaign-error" className="text-sm font-medium text-red-600">{error}</p>}

        <div className="flex items-center gap-3">
          <Button data-testid="campaign-create" type="submit" variant="primary" disabled={disabled || saving}>
            {saving ? "Creating…" : "Create as draft"}
          </Button>
          <Link to="/app/campaigns"><Button type="button" variant="ghost">Cancel</Button></Link>
        </div>
      </form>
    </div>
  );
}
